import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts';
import StatsCard from '../components/dashboard/StatsCard';
import { useTheme } from '../contexts/ThemeContext';

const useQuery = () => {
    return new URLSearchParams(window.location.search);
}

const DriverGraph = () => {
    const { isDark } = useTheme();
    const query = useQuery();
    const driverId = query.get('driver');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (driverId) {
            fetchDriverStats();
        }
    }, [driverId]);

    const fetchDriverStats = async () => {
        try {
            const response = await fetch(`/api/v1/driver_stats/${driverId}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                }
            });
            const jsonData = await response.json();
            setData(jsonData);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching driver stats:", error);
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return `GHC ${parseFloat(amount || 0).toLocaleString()}`;
    };

    const styles = {
        container: {
            padding: '2rem',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            color: isDark ? '#fff' : '#1f2937'
        },
        header: {
            marginBottom: '2rem'
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem'
        },
        sectionTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1rem',
            marginTop: '2rem',
            borderLeft: `4px solid ${isDark ? '#10b981' : '#059669'}`,
            paddingLeft: '1rem'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        chartCard: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.5rem'
        }
    };

    if (loading) return <div style={styles.loading}>Loading driver statistics...</div>;
    if (!data) return <div style={styles.loading}>No data found for this driver.</div>;

    const { driver, car, metrics, charts } = data;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Full Details For {driver.first_name} {driver.last_name}</h1>
                <p>Car: {car?.model} - {car?.car_number}</p>
                {(car?.image_url || car?.car_image_link) && (
                    <div style={{ marginTop: '1.5rem' }}>
                        <img
                            src={car.image_url || car.car_image_link}
                            alt={`${car.model} - ${car.car_number}`}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '400px',
                                borderRadius: '1rem',
                                boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                objectFit: 'cover'
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Metrics Grid */}
            <div style={styles.grid}>
                <StatsCard title="Days Done" value={metrics.days_done} icon="✅" color="blue" />
                <StatsCard title="Days Remaining" value={metrics.days_remaining} icon="⏳" color="orange" />
                <StatsCard title="Percentage Done" value={`${((metrics.days_done / metrics.total_days) * 100).toFixed(2)}%`} icon="📈" color="green" />
            </div>

            <div style={styles.grid}>
                <StatsCard title="This Month Sales" value={formatCurrency(metrics.this_month_sales)} icon="💰" color="purple" />
                <StatsCard title="All Time Sales" value={formatCurrency(metrics.all_time_sales)} icon="🏛️" color="indigo" />
            </div>

            <div style={styles.grid}>
                <StatsCard title="This Month Expenses" value={formatCurrency(metrics.this_month_expenses)} icon="💸" color="red" />
                <StatsCard title="All Time Expenses" value={formatCurrency(metrics.all_time_expenses)} icon="📉" color="pink" />
            </div>

            <div style={styles.grid}>
                <StatsCard title="Loan Borrowed" value={formatCurrency(metrics.loan_borrowed)} icon="🏦" color="yellow" />
                <StatsCard title="Loan (Days Equivalent)" value={`${metrics.loan_in_days} Days`} icon="📅" color="cyan" />
            </div>

            {/* Charts */}
            <h2 style={styles.sectionTitle}>Overall Sales</h2>
            <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={charts.all_time_sales}>
                        <defs>
                            <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#eee'} />
                        <XAxis dataKey="date" stroke={isDark ? '#888' : '#666'} />
                        <YAxis stroke={isDark ? '#888' : '#666'} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#333' : '#fff', borderColor: isDark ? '#555' : '#ccc' }} />
                        <Area type="monotone" dataKey="amount" stroke="#8884d8" fillOpacity={1} fill="url(#colorSales)" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            <h2 style={styles.sectionTitle}>This Month's Sales</h2>
            <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={charts.this_month_sales}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#eee'} />
                        <XAxis dataKey="date" stroke={isDark ? '#888' : '#666'} />
                        <YAxis stroke={isDark ? '#888' : '#666'} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#333' : '#fff', borderColor: isDark ? '#555' : '#ccc' }} />
                        <Line type="monotone" dataKey="amount" stroke="#82ca9d" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <h2 style={styles.sectionTitle}>This Month's Expenses</h2>
            <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={charts.this_month_expenses}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#eee'} />
                        <XAxis dataKey="date" stroke={isDark ? '#888' : '#666'} />
                        <YAxis stroke={isDark ? '#888' : '#666'} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#333' : '#fff', borderColor: isDark ? '#555' : '#ccc' }} />
                        <Line type="monotone" dataKey="amount" stroke="#ff7300" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>


        </div>
    );
};

export default DriverGraph;
